import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Save, Sparkles } from "lucide-react";

const TOKENS = ["{client_name}", "{coach_name}"];

export default function CheckInTemplate() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("chk.meta_title"));
  const [tpl, setTpl] = useState(null);
  const [isDefault, setIsDefault] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/check-in-template"); setTpl(data?.template || ""); setIsDefault(Boolean(data?.is_default)); }
    catch { setTpl(""); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const save = async () => {
    if ((tpl || "").trim().length < 10) { toast.error(t("chk.err_short")); return; }
    setSaving(true);
    try { await api.patch("/coach/check-in-template", { template: tpl.trim() }); setIsDefault(false); toast.success(t("chk.saved")); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const insert = (token) => setTpl((prev) => `${prev || ""}${(prev || "").endsWith(" ") || !prev ? "" : " "}${token}`);

  const preview = (tpl || "").replace("{client_name}", "Maya").replace("{coach_name}", user?.name?.split(" ")[0] || "Coach");

  return (
    <div data-testid="coach-checkin-template-page">
      <Seo title={t("chk.seo")} noindex />
      <Link to="/app/coach/settings" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="chk-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.title")}
      </Link>
      <PageHeader eyebrow={t("chk.eyebrow")} title={t("chk.title")} subtitle={t("chk.subtitle")} />

      {tpl === null ? (
        <Skeleton className="h-40 rounded-2xl" />
      ) : (
        <div className="space-y-4">
          {isDefault && <p className="rounded-xl bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="chk-default-hint">{t("chk.default_hint")}</p>}
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("chk.f_template")}</label>
            <Textarea className="min-h-[140px]" value={tpl} onChange={(e) => setTpl(e.target.value)} maxLength={1000} data-testid="chk-template" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {TOKENS.map((tok) => (
                <button key={tok} onClick={() => insert(tok)} data-testid={`chk-token-${tok.replace(/[{}]/g, "")}`} className="rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent">+ {tok}</button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="chk-preview">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Sparkles className="h-3.5 w-3.5 text-primary" /> {t("chk.preview")}</p>
            <p className="whitespace-pre-wrap text-sm text-foreground">{preview}</p>
          </div>
          <Button className="w-full gap-2 rounded-full" onClick={save} disabled={saving} data-testid="chk-save"><Save className="h-4 w-4" /> {saving ? t("common.saving") : t("chk.save")}</Button>
        </div>
      )}
    </div>
  );
}
